import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './api-error.js';
import { sortQuerySchema } from './sort.js';

/**
 * Roles, ordered least to most privileged. Middleware compares by index, so the
 * order of this array is the privilege ladder.
 *
 * @since 1.0.0
 */
export const ROLES = ['viewer', 'dev', 'admin'] as const;

export const roleSchema = z.enum(ROLES);

export type Role = z.infer<typeof roleSchema>;

/**
 * Returns true when a role meets or exceeds the required role.
 *
 * @since 1.0.0
 * @param role - the role held by the session user
 * @param required - the minimum role the route demands
 * @returns whether access is allowed
 */
export function hasRole(role: Role, required: Role): boolean {
    return ROLES.indexOf(role) >= ROLES.indexOf(required);
}

export const loginRequestSchema = z.object({
    email: z.email().max(320),
    password: z.string().min(1).max(512),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const sessionUserSchema = z.object({
    id: z.number().int(),
    email: z.email(),
    name: z.string(),
    role: roleSchema,
});

export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * Shortest password a dashboard user may set.
 *
 * @since 1.3.0
 */
export const MIN_PASSWORD_LENGTH = 12;

export const createUserRequestSchema = z.object({
    email: z.email().max(320),
    name: z.string().min(1).max(120),
    role: roleSchema,
    password: z.string().min(MIN_PASSWORD_LENGTH).max(512),
});

export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const updateUserRequestSchema = z
    .object({
        name: z.string().min(1).max(120).optional(),
        role: roleSchema.optional(),
        password: z.string().min(MIN_PASSWORD_LENGTH).max(512).optional(),
        isDisabled: z.boolean().optional(),
    })
    .refine((value) => Object.keys(value).length > 0, { message: 'No fields to update' });

export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const userSchema = sessionUserSchema.extend({
    createdAt: z.number().int(),
    disabledAt: z.number().int().nullable(),
});

export type User = z.infer<typeof userSchema>;

/**
 * How the auth routes wrap the signed-in user.
 *
 * @since 1.3.0
 */
export const sessionUserEnvelopeSchema = z.object({
    user: sessionUserSchema,
});

export type SessionUserEnvelope = z.infer<typeof sessionUserEnvelopeSchema>;

/**
 * How the user routes wrap a created or updated user.
 *
 * @since 1.3.0
 */
export const userEnvelopeSchema = z.object({
    user: userSchema,
});

export type UserEnvelope = z.infer<typeof userEnvelopeSchema>;

/**
 * One page of the user roster.
 *
 * @since 1.3.0
 */
export const usersPageSchema = paginatedSchema(userSchema);

export type UsersPage = z.infer<typeof usersPageSchema>;

/**
 * Columns the user roster may be sorted on, named after the table's own column keys.
 *
 * @since 1.4.0
 */
export const USER_SORT_KEYS = ['name', 'email', 'role', 'added', 'status'] as const;

export const userSortKeySchema = z.enum(USER_SORT_KEYS);

export type UserSortKey = z.infer<typeof userSortKeySchema>;

export const userQuerySchema = paginationQuerySchema.extend(sortQuerySchema(USER_SORT_KEYS, 'name', 'asc').shape);

export type UserQuery = z.infer<typeof userQuerySchema>;
